import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, TextField, Typography } from '@mui/material';
import { EventOutlined, PaymentsOutlined } from '@mui/icons-material';
import { CreateExpenseRequest, Expense, PaymentMethod, UpdateExpenseRequest } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { AppDialogFormActions } from '../common/AppDialog';

interface ExpenseFormProps {
  expense?: Expense;
  onSubmit: (data: CreateExpenseRequest | UpdateExpenseRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, onSubmit, onCancel, isLoading = false }) => {
  const { t } = useLanguage();
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [expenseDate, setExpenseDate] = useState(today());
  const [paid, setPaid] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | ''>('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setDescription(expense?.description ?? '');
    setCategory(expense?.category ?? '');
    setAmount(expense?.amount != null ? String(expense.amount) : '');
    setExpenseDate(expense?.expenseDate ?? today());
    setPaid(expense?.paid ?? false);
    setPaymentMethod(expense?.paymentMethod ?? '');
    setNotes(expense?.notes ?? '');
    setErrors({});
  }, [expense]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!description.trim()) nextErrors.description = t('expenses.descriptionRequired');
    const parsedAmount = Number(amount.replace(',', '.'));
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) nextErrors.amount = t('expenses.amountRequired');
    if (!expenseDate) nextErrors.expenseDate = t('expenses.dateRequired');
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    await onSubmit({
      description: description.trim(),
      category: category.trim() || undefined,
      amount: parsedAmount,
      expenseDate,
      paid,
      paymentMethod: paymentMethod || undefined,
      notes: notes.trim() || undefined,
    });
  };

  const paymentLabels: Record<PaymentMethod, string> = {
    [PaymentMethod.CASH]: t('expenses.cash'),
    [PaymentMethod.CARD]: t('expenses.card'),
    [PaymentMethod.BANK_TRANSFER]: t('expenses.bankTransfer'),
    [PaymentMethod.CHECK]: t('expenses.check'),
    [PaymentMethod.OTHER]: t('expenses.other'),
  };

  return (
    <Box component="form" onSubmit={submit} sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Typography variant="body2" color="text.secondary">{t('expenses.formHint')}</Typography>
      <TextField label={t('expenses.description')} placeholder={t('expenses.descriptionPlaceholder')} value={description}
        onChange={e => setDescription(e.target.value)} error={!!errors.description} helperText={errors.description} required autoFocus />
      <TextField label={t('expenses.category')} placeholder={t('expenses.categoryPlaceholder')} value={category} onChange={e => setCategory(e.target.value)} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <TextField label={t('expenses.amount')} value={amount} onChange={e => setAmount(e.target.value)} error={!!errors.amount} helperText={errors.amount || t('expenses.amountHint')} type="text" inputMode="decimal" InputProps={{ startAdornment: <PaymentsOutlined sx={{ mr: 1, color: 'primary.main' }} /> }} required />
        <TextField label={t('expenses.date')} value={expenseDate} onChange={e => setExpenseDate(e.target.value)} error={!!errors.expenseDate} helperText={errors.expenseDate} type="date" InputLabelProps={{ shrink: true }} InputProps={{ startAdornment: <EventOutlined sx={{ mr: 1, color: 'primary.main' }} /> }} required />
      </Box>
      <FormControl fullWidth>
        <InputLabel>{t('expenses.paymentMethod')}</InputLabel>
        <Select label={t('expenses.paymentMethod')} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as PaymentMethod)}>
          <MenuItem value="">—</MenuItem>
          {Object.values(PaymentMethod).map(method => <MenuItem key={method} value={method}>{paymentLabels[method]}</MenuItem>)}
        </Select>
      </FormControl>
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
        <FormControlLabel control={<Checkbox checked={paid} onChange={e => setPaid(e.target.checked)} />} label={<Box><Typography fontWeight={700}>{t('expenses.paid')}</Typography><Typography variant="body2" color="text.secondary">{t('expenses.paidHint')}</Typography></Box>} />
      </Paper>
      <TextField label={t('expenses.notes')} value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2} />
      <AppDialogFormActions>
        <Button onClick={onCancel} disabled={isLoading}>{t('common.cancel')}</Button>
        <Button type="submit" variant="contained" loading={isLoading}>{expense ? t('common.update') : t('common.save')}</Button>
      </AppDialogFormActions>
    </Box>
  );
};

export default ExpenseForm;
